'use client';

import { useState, useEffect, useCallback } from 'react';
import type { Review, NewReviewForm } from '@/types/review';
import { wordpressApi } from '@/services/wordpress-api';
import { isMarkedAsHelpful, saveMarkAsHelpful } from '@/utils/helpful-uitils';

interface UseReviewsReturn {
  reviews: Review[];
  loading: boolean;
  error: string | null;
  refreshReviews: () => Promise<void>;
  submitReview: (reviewData: NewReviewForm) => Promise<void>;
  markHelpful: (reviewId: string) => Promise<void>;
  isMockMode: boolean;
}

export function useReviews(productKey: string): UseReviewsReturn {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isMockMode = wordpressApi.isMock();

  const refreshReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedReviews = await wordpressApi.getReviews(productKey);
      // Filter for approved reviews only
      const approvedReviews = fetchedReviews.filter(
        (review) =>
          review.status === 'approved' && review.productKey === productKey
      );

      setReviews(approvedReviews);

      if (isMockMode) {
        console.log(
          `🎭 Mock Mode: Loaded ${approvedReviews.length} reviews for ${productKey}`
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error loading reviews:', err);
    } finally {
      setLoading(false);
    }
  }, [productKey, isMockMode]);

  const submitReview = useCallback(
    async (reviewData: NewReviewForm) => {
      try {
        const newReview = await wordpressApi.submitReview(
          reviewData,
          productKey
        );

        // Add the new review to the beginning of the list if it's approved
        if (newReview.status === 'approved') {
          setReviews((prev) => [newReview, ...prev]);
        }

        if (isMockMode) {
          console.log('🎭 Mock Mode: Review submitted successfully', newReview);
        }

        return newReview;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to submit review';
        throw new Error(errorMessage);
      }
    },
    [productKey, isMockMode]
  );

  const markHelpful = useCallback(
    async (reviewId: string) => {
      try {
        // Check in localStorage if the user has already marked this review as helpful
        if (isMarkedAsHelpful(reviewId)) {
          console.log('Review already marked as helpful:', reviewId);
          return;
        }
        // Add the reviewId to localStorage
        saveMarkAsHelpful(reviewId);
        // Update the helpful count in the local state even if the API call fails
        setReviews((prev) =>
          prev.map((review) =>
            review.id === reviewId
              ? { ...review, helpful: review.helpful + 1 }
              : review
          )
        );
        if (isMockMode) {
          console.log('🎭 Mock Mode: Marked review as helpful', reviewId);
        }
        const result = await wordpressApi.markHelpful(reviewId);
      } catch (err) {
        console.error('Error marking review as helpful:', err);
        // Optionally show a toast notification here
      }
    },
    [isMockMode]
  );

  // Load reviews on mount and when productKey changes
  useEffect(() => {
    if (productKey) {
      refreshReviews();
    } else {
      console.warn('No product name provided');
    }
  }, [productKey, refreshReviews]);

  return {
    reviews,
    loading,
    error,
    refreshReviews,
    submitReview,
    markHelpful,
    isMockMode,
  };
}
